import { cn, Table, TableSort, useSort } from "@kyan/ui";
import { AnimatePresence, motion } from "motion/react";

import { EmptyState } from "#/components/Contents/components/EmptyState/EmptyState";
import { useAutoExpandRows } from "#/hooks/useAutoExpandRows";
import { usePositionsDrawerMarket } from "#/store/app/positionsDrawer/positionsDrawerMarket";
import { useSharePnlCardsModal } from "#/store/app/sharePnlCardModal/sharePnlCardsModal";
import { usePositions } from "#/store/wallet/account/positions";
import { type Position } from "#/types/positions";

import {
  defaultSort,
  getInnerColumns,
  getOuterColumns,
} from "./OptionPositions.constants";
import { getOptionPositionRows } from "./OptionPositions.utils";

export interface OptionPositionsProps {
  sort?: TableSort<Position>;
  isAllVariant?: boolean;
  isPortfolioVariant?: boolean;
  onSortChange?: (sort: TableSort<Position>) => void;
}

export const OptionPositions: React.FC<OptionPositionsProps> = ({
  sort: controlledSort,
  isAllVariant,
  isPortfolioVariant,
  onSortChange,
}) => {
  const { positions } = usePositions();
  const { open } = useSharePnlCardsModal();
  const { market } = usePositionsDrawerMarket();
  const { sort, setSort } = useSort<Position>({
    defaultSort,
    controlledSort,
    onSortChange,
  });

  const { rows, dataLength } = getOptionPositionRows(
    positions,
    sort,
    isAllVariant,
  );
  const isEmpty = !rows.length;

  const { openRowIds, onRowClick } = useAutoExpandRows<Position>({
    dataLength,
  });

  const excludeColumnIds = isAllVariant ? [] : ["accruedFunding"];
  const outerColumns = getOuterColumns({
    excludeColumnIds,
    openSharePnlCardsModal: open,
  });
  const innerColumns = getInnerColumns({
    excludeColumnIds,
    openSharePnlCardsModal: open,
  });

  return (
    <Table
      isServerSide
      data={rows}
      sort={sort}
      columns={outerColumns}
      openRowIds={openRowIds}
      defaultSort={defaultSort}
      hideHeader={isAllVariant}
      emptyState={
        <EmptyState
          underlying={market.underlying}
          shouldShowAction={isPortfolioVariant}
          description={`Your ${market.underlying} option positions will show here`}
        />
      }
      classes={{
        wrapper: cn("bg-background flex flex-col", {
          "flex-1": !isAllVariant && !isEmpty,
          "h-full": isEmpty,
        }),
        table: cn("flex w-fit flex-col", {
          "flex border-b-0": isAllVariant,
          "flex-1 overflow-hidden": !isAllVariant && !isEmpty,
          "h-full": isEmpty,
          "border-b-0": isEmpty && !isPortfolioVariant,
        }),
        thead: "sticky top-0 bg-buttonBg border-b border-divider",
        tbody: cn("text-textPrimary", {
          "flex-1": isEmpty,
          "overflow-y-auto": !isEmpty && !isAllVariant,
          "border-divider border-b":
            !isEmpty && !isAllVariant && !isPortfolioVariant,
        }),
        row: cn("bg-buttonHover cursor-pointer", {
          "first:border-t-0": !isAllVariant,
        }),
        loading: {
          wrapper: "first:border-t-0",
        },
        cellClassName: "py-1.5",
      }}
      onSortChange={setSort}
      onRowClick={onRowClick}
      renderRowExpansion={({ row: { subPositions = [] }, isOpen }) => (
        <AnimatePresence>
          {isOpen && subPositions.length ? (
            <motion.div
              exit={{ height: 0 }}
              initial={{ height: 0 }}
              className="overflow-hidden"
              transition={{ duration: 0.3 }}
              animate={{ height: Math.min(subPositions.length * 33, 400) }} // 400 is the max height set in TableVirtualizationWrapper
            >
              <Table
                hideHeader
                data={subPositions}
                columns={innerColumns}
                virtualization={{
                  estimateSize: 33,
                  overscan: 12,
                }}
                classes={{
                  table: "border-b-0",
                  tbody: "text-textPrimary",
                  row: "bg-background h-8.25",
                  cellClassName: "py-1.5",
                }}
              />
            </motion.div>
          ) : null}
        </AnimatePresence>
      )}
    />
  );
};
